import React from "react";
import { PropTypes } from "prop-types";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Button, Hidden, Link, Typography } from "@mui/material";
import ellipseBgImg from "../../assets/svgs/courseOverviewEllipse.svg";
import CourseTypeIcon from "../Icons/CourseTypeIcon";
import { PlayCircleOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const getDuration = (trainingHours, t) => {
  const hours = Math.floor(trainingHours / 60);
  const minutes = !!hours ? trainingHours % 60 : trainingHours;
  return (
    <Box display="inline-flex" alignItems="flex-end">
      {!!hours && (
        <>
          <Typography
            variant="h3"
            color="primary"
            sx={{ pr: !!minutes ? 0.5 : 1 }}
          >
            {hours}
          </Typography>
          <Typography
            variant="h6"
            color="primary"
            sx={{ pr: !!minutes ? 1 : 0, lineHeight: 1.5 }}
          >
            {!!minutes
              ? t("courseOverview.hoursWithMinutesAbbrev")
              : hours > 1
              ? t("courseOverview.hoursAbbrev")
              : t("courseOverview.hourAbbrev")}
          </Typography>
        </>
      )}
      {!!minutes && (
        <>
          <Typography
            variant="h3"
            color="primary"
            sx={{ pr: !!hours ? 0.5 : 1 }}
          >
            {minutes}
          </Typography>
          <Typography variant="h6" color="primary" sx={{ lineHeight: 1.5 }}>
            {!!hours
              ? t("courseOverview.minutesWithHoursAbbrev")
              : t("courseOverview.minAbbrev")}
          </Typography>
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    background: `linear-gradient(64.48deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
  },
  container: {
    background: `url(${ellipseBgImg}) no-repeat`,
    padding: theme.spacing(5, 8, 8, 8),
    flex: 1,
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(3)
    }
  },
  header: {
    color: theme.palette.common.white
  },
  imageContainer: {
    backgroundSize: "contain",
    backgroundImage: ({ courseImage }) => `url(${courseImage})`,
    backgroundRepeat: "no-repeat",
    maxWidth: "100%",
    marginTop: theme.spacing(-21.5),
    padding: "50%",
    maxHeight: 0,
    [theme.breakpoints.down("md")]: {
      padding: "20%"
    }
  },
  viewCourseOutlineBtn: {
    marginBottom: theme.spacing(3.5)
  },
  joinMeetingBtn: {
    width: "100%"
  }
}));

const CourseInfoContainer = ({ children, text }) => {
  const theme = useTheme();
  return (
    <Box
      height={92}
      width={124}
      bgcolor={theme.palette.primary.light}
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={1}
      mr={2}
      mb={{ xs: 3, sm: 0 }}
    >
      <Typography variant="overline" sx={{ pb: 1 }}>
        {text}
      </Typography>
      {children}
    </Box>
  );
};

const CourseOverviewPage = ({
  description,
  duration,
  overviewText,
  courseType,
  image,
  passingScore,
  onViewOutlineClick,
  onGetStartedClick,
  joinUrl,
  passFail
}) => {
  const classes = useStyles({ courseImage: image });
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box className={classes.root} position="relative">
      <Box
        display="flex"
        height="100%"
        width="100%"
        className={classes.container}
      >
        <Box display="flex" flexDirection="column" flex={1}>
          <Typography variant="h1" className={classes.header} sx={{ mt: 3 }}>
            {t("courseOverview.gettingStartedTitle")}
          </Typography>
          <Box
            bgcolor={theme.palette.common.white}
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            flex={1}
            p={{ xs: 2, sm: 6.25 }}
            mt={5.25}
          >
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              mr={{ xs: 0, md: 6 }}
            >
              {description && (
                <>
                  <Typography variant="h5" sx={{ pb: 1.25 }}>
                    {t("courseOverview.courseOverviewHeader")}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {description}
                  </Typography>
                </>
              )}
              <Typography variant="h5" sx={{ pb: 1.25, pt: 3 }}>
                {t("courseOverview.courseInstructionsHeader")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ pb: 4.5 }}
                color="textSecondary"
              >
                <div dangerouslySetInnerHTML={{ __html: overviewText }} />
              </Typography>
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems="center"
              >
                <CourseInfoContainer text={t("courseOverview.courseTypeLabel")}>
                  <Box>
                    <CourseTypeIcon
                      type={courseType}
                      size="large"
                      color="primary"
                      style={{ paddingTop: theme.spacing(0.25) }}
                    />
                  </Box>
                </CourseInfoContainer>
                <CourseInfoContainer text={t("courseOverview.durationLabel")}>
                  {getDuration(duration, t)}
                </CourseInfoContainer>
                <CourseInfoContainer
                  text={t("courseOverview.passingScoreLabel")}
                >
                  <Typography variant="h3" sx={{ pr: 1 }} color="primary">
                    {passFail
                      ? t("courseOverview.noPassingScore")
                      : passingScore}
                  </Typography>
                </CourseInfoContainer>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row", md: "column" }}
              flex={1}
              maxWidth={480}
              mr={{ xs: 0, sm: -9.25 }}
              mt={{ xs: 0, sm: 8, md: 0 }}
            >
              {/* TODO: Fix course image for mobile screen size */}
              <Hidden smDown>
                <Box
                  className={classes.imageContainer}
                  flex={{ xs: "unset", md: 1 }}
                  alignSelf="flex-end"
                />
              </Hidden>
              <Box
                display="flex"
                flexDirection="column"
                maxWidth={{ xs: "unset", sm: "65%" }}
                mt={5}
                ml={{ xs: 0, sm: 3, md: 9.25 }}
              >
                <Button
                  color="primary"
                  variant="outlined"
                  className={classes.viewCourseOutlineBtn}
                  onClick={() => onViewOutlineClick()}
                >
                  <Typography variant="h5" sx={{ p: 1 }}>
                    {t("courseOverview.viewCourseOutlineBtn")}
                  </Typography>
                </Button>
                {joinUrl ? (
                  <Link href={joinUrl} target="_blank">
                    <Button
                      color="primary"
                      variant="contained"
                      size="large"
                      className={classes.joinMeetingBtn}
                      endIcon={<PlayCircleOutline fontSize="large" />}
                    >
                      <Typography variant="h5" sx={{ p: 1 }}>
                        {t("courseOverview.joinMeetingBtn")}
                      </Typography>
                    </Button>
                  </Link>
                ) : (
                  <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<PlayCircleOutline fontSize="large" />}
                    onClick={() => onGetStartedClick()}
                  >
                    <Typography variant="h5" sx={{ p: 1 }}>
                      {t("courseOverview.getStartedBtn")}
                    </Typography>
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CourseOverviewPage;

CourseOverviewPage.propTypes = {
  description: PropTypes.string,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overviewText: PropTypes.string,
  courseType: PropTypes.string,
  image: PropTypes.string,
  passingScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onViewOutlineClick: PropTypes.func,
  onGetStartedClick: PropTypes.func,
  joinUrl: PropTypes.string,
  passFail: PropTypes.bool
};
